import { useQuery } from '@apollo/react-hooks'
import { ApolloConsumer } from '@apollo/react-common'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core'
import { GetApp } from '@material-ui/icons'
import { gql } from 'apollo-boost'
import { LoadMask, useToasts } from '@vestaboard/installables'
import moment from 'moment'
import React, { useState } from 'react'
import { csvDownload, csvFormat } from '../../utils'
import { FormattedDateTime } from '../formatted-date-time'
import { TruncatedId } from '../truncated-id'
import { PlusRenewalFailureSortBy, PlusRenewalFailureSortOrder } from './types'
import {
  ListPlusRenewalFailures as ListPlusRenewalFailuresQuery,
  ListPlusRenewalFailuresVariables,
  ListPlusRenewalFailures_listPlusRenewalErrors_plusRenewalFailures
} from './__generated__/ListPlusRenewalFailures'
import { SuperGrid } from '../../ui/SuperGrid'

const QUERY = gql`
  query ListPlusRenewalFailures(
    $terms: String
    $page: Int
    $perPage: Int
    $sortBy: PlusRenewalFailureSortBy
    $sortOrder: PlusRenewalFailureSortOrder
  ) {
    listPlusRenewalErrors(terms: $terms, perPage: $perPage, page: $page, sortBy: $sortBy, sortOrder: $sortOrder) {
      plusRenewalFailures {
        id
        errorCode
        errorMessage
        created
        productApplication {
          id
          board {
            id
            title
          }
        }
        details
      }
      pagination {
        page
        perPage
        total
      }
    }
  }
`

interface FlatFailure {
  id: string
  created: number
  productApplicationId: string
  errorCode: string
  errorMessage: string
  details: string
  boardId: string
  boardTitle: string
}

const columns = [
  {
    title: 'Failure ID',
    key: PlusRenewalFailureSortBy.id,
    render: (item: FlatFailure) => <TruncatedId value={item.id} />
  },
  {
    title: 'Occurred At',
    key: PlusRenewalFailureSortBy.created,
    render: (item: FlatFailure) => <FormattedDateTime format='MM/DD/YYYY' value={item.created} />
  },
  {
    title: 'Previous Subscription ID',
    key: PlusRenewalFailureSortBy.productApplicationId,
    render: (item: FlatFailure) =>
      item.productApplicationId ? (
        <TruncatedId value={item.productApplicationId} path={`/plus/${item.productApplicationId}`} />
      ) : null
  },
  {
    title: 'Board ID',
    render: (item: FlatFailure) =>
      item.boardId ? <TruncatedId value={item.boardId} path={`/boards/${item.boardId}`} /> : null
  },
  {
    title: 'Board Title',
    render: (item: FlatFailure) => (item.boardTitle ? item.boardTitle : null)
  },
  {
    title: 'Error Code',
    key: PlusRenewalFailureSortBy.errorCode,
    render: (item: FlatFailure) => item.errorCode
  },
  {
    title: 'Error Message',
    key: PlusRenewalFailureSortBy.errorMessage,
    render: (item: FlatFailure) => item.errorMessage
  }
]

export const ListPlusRenewalFailures = () => {
  const perPage = 50
  const [sort, setSort] = useState<{
    by: PlusRenewalFailureSortBy
    order: PlusRenewalFailureSortOrder
  }>({
    by: PlusRenewalFailureSortBy.created,
    order: PlusRenewalFailureSortOrder.desc
  })
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportDateMin, setExportDateMin] = useState('')
  const [exportDateMax, setExportDateMax] = useState('')
  const [exporting, setExporting] = useState(false)
  const { addToast } = useToasts()
  const variables = {
    terms: (searchValue || '').trim(),
    sortBy: sort.by,
    sortOrder: sort.order
  }
  const { data, loading, error } = useQuery<ListPlusRenewalFailuresQuery, ListPlusRenewalFailuresVariables>(QUERY, {
    variables: {
      ...variables,
      perPage,
      page
    }
  })

  const plusRenewalFailures = data?.listPlusRenewalErrors.plusRenewalFailures || []
  const pagination = data?.listPlusRenewalErrors?.pagination

  const createFlatFailure = (input: ListPlusRenewalFailures_listPlusRenewalErrors_plusRenewalFailures) =>
    ({
      id: input.id,
      created: input.created,
      productApplicationId: input.productApplication?.id,
      errorCode: input.errorCode,
      errorMessage: input.errorMessage,
      boardId: input.productApplication?.board?.id,
      boardTitle: input.productApplication?.board?.title
    } as FlatFailure)

  const items = (plusRenewalFailures || []).map(createFlatFailure)

  return (
    <SuperGrid
      title='Plus Renewal Failures'
      setSort={setSort}
      columns={columns}
      rows={items}
      sortBy={sort.by}
      sortOrder={sort.order}
      pagination={pagination}
      setPage={setPage}
      error={!!error}
      loading={loading}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      centerButtons={
        <Box display='flex'>
          <Button variant='outlined' onClick={() => setExportDialogOpen(true)}>
            Export CSV
          </Button>
          <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
            <DialogTitle>Export Plus Renewal Failures</DialogTitle>
            <DialogContent>
              <Box display='flex' flexDirection='column' style={{ gap: 16, minWidth: 300 }}>
                <TextField
                  variant='standard'
                  type='date'
                  label='Date From'
                  value={exportDateMin}
                  onChange={e => setExportDateMin(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  variant='standard'
                  type='date'
                  label='Date To'
                  value={exportDateMax}
                  onChange={e => setExportDateMax(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  onClick={() => {
                    setExportDateMin('')
                    setExportDateMax('')
                  }}>
                  CLEAR DATES (Full Report)
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
              <ApolloConsumer>
                {apollo => (
                  <>
                    {exporting ? <LoadMask /> : null}
                    <Button
                      endIcon={<GetApp />}
                      variant='outlined'
                      onClick={async () => {
                        setExporting(true)
                        const dateMin = exportDateMin ? moment(exportDateMin).startOf('day').valueOf() : 0
                        const dateMax = exportDateMax ? moment(exportDateMax).endOf('day').valueOf() : Infinity
                        const hasDateFilter = !!(exportDateMin || exportDateMax)
                        const exportPerPage = 100
                        const results: any[] = []

                        try {
                          let currentPage = 0
                          let done = false

                          while (!done) {
                            const result = await apollo.query({
                              fetchPolicy: 'network-only',
                              query: QUERY,
                              variables: {
                                ...variables,
                                sortBy: PlusRenewalFailureSortBy.created,
                                sortOrder: PlusRenewalFailureSortOrder.desc,
                                perPage: exportPerPage,
                                page: currentPage
                              }
                            })

                            const failures = result.data.listPlusRenewalErrors.plusRenewalFailures
                            const pagination = result.data.listPlusRenewalErrors.pagination

                            for (const failure of failures) {
                              const flat = createFlatFailure(failure)
                              const created = flat.created || 0

                              // Since sorted desc, if we're past dateMax, skip ahead
                              if (hasDateFilter && created > dateMax) continue
                              // If we've gone below dateMin, we're done
                              if (hasDateFilter && created < dateMin) {
                                done = true
                                break
                              }

                              results.push({
                                ...flat,
                                created: flat.created ? moment(flat.created).format('YYYY-MM-DD') : null
                              })
                            }

                            // Stop if we've fetched all pages
                            if ((currentPage + 1) * exportPerPage >= pagination.total) {
                              done = true
                            }

                            currentPage++
                          }

                          if (results.length > 0) {
                            const csv = csvFormat(results, Object.keys(results[0]))
                            csvDownload(csv)
                          } else {
                            addToast('No records found in the selected date range', { appearance: 'error' })
                          }
                        } catch (e) {
                          console.error(e)
                          addToast('Error exporting CSV', { appearance: 'error' })
                        }

                        setExporting(false)
                      }}>
                      Export CSV
                    </Button>
                  </>
                )}
              </ApolloConsumer>
            </DialogActions>
          </Dialog>
        </Box>
      }
    />
  )
}
